import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../components/atlas/helpers";
import { Scale, TrendingUp, AlertTriangle, Activity, ShieldCheck, DollarSign } from "lucide-react";

export function StatArbWorkspace() {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchState = async () => {
      try {
        const res = await axios.get(`${API}/stat-arb/state`);
        setState(res.data);
        setError(null);
      } catch (err) {
        setError("Daemon disconnected or starting up...");
      }
    };
    fetchState();
    const interval = setInterval(fetchState, 2000);
    return () => clearInterval(interval);
  }, []);

  if (error) {
    return (
      <div className="p-8 h-full flex flex-col items-center justify-center text-center">
        <Scale className="w-16 h-16 text-slate-500 mb-4 opacity-50" />
        <h2 className="text-xl font-bold text-slate-300 mb-2">Statistical Arbitrage Engine Offline</h2>
        <p className="text-slate-500">{error}</p>
        <p className="text-slate-600 text-sm mt-4">Please ensure stat_arb_daemon.py is running.</p>
      </div>
    );
  }

  if (!state) {
    return <div className="p-8 text-slate-400">Loading Quantitative Models...</div>;
  }

  const isHalted = state.regime.includes("HALT");
  const isShort = state.in_trade === -1;
  const isLong = state.in_trade === 1;
  const isFlat = state.in_trade === 0;

  return (
    <div className="p-8 h-full flex flex-col gap-6" style={{ overflowY: 'auto' }}>
      <header className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white mb-2 flex items-center gap-3">
            <Scale className="text-blue-500" />
            Statistical Arbitrage
          </h1>
          <p className="text-slate-400">AVAX/SOL • Kalman Filter • Ornstein-Uhlenbeck • HMM Regime Classification</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-slate-500 mb-1">Live Capital (10x Lev)</div>
          <div className="text-2xl font-bold text-emerald-400 font-mono">
            ${state.capital.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </header>

      {/* Primary Telemetry */}
      <div className="grid grid-cols-4 gap-4">
        {/* ML Regime Classifier */}
        <div className={`p-5 rounded-xl border ${isHalted ? 'bg-red-500/10 border-red-500/30' : 'bg-emerald-500/10 border-emerald-500/30'} flex flex-col justify-between`}>
          <div className="flex items-center gap-2 mb-2">
            {isHalted ? <AlertTriangle className="w-5 h-5 text-red-500" /> : <ShieldCheck className="w-5 h-5 text-emerald-500" />}
            <span className="text-sm font-semibold uppercase tracking-wider text-slate-300">HMM Regime</span>
          </div>
          <div>
            <div className={`text-2xl font-bold ${isHalted ? 'text-red-400' : 'text-emerald-400'}`}>
              {state.regime}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Vol: {state.volatility.toFixed(6)} / Thr: {state.vol_threshold.toFixed(6)}
            </div>
          </div>
        </div>

        {/* Kalman Filter Hedge Ratio */}
        <div className="p-5 rounded-xl bg-slate-800/50 border border-slate-700/50 flex flex-col justify-between">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 text-blue-500" />
            <span className="text-sm font-semibold uppercase tracking-wider text-slate-300">Kalman Beta</span>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-400 font-mono">
              {state.kalman_beta.toFixed(4)}
            </div>
            <div className="text-xs text-slate-500 mt-1">Dynamic State-Space Ratio</div>
          </div>
        </div>

        {/* OU Z-Score */}
        <div className="p-5 rounded-xl bg-slate-800/50 border border-slate-700/50 flex flex-col justify-between">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-purple-500" />
            <span className="text-sm font-semibold uppercase tracking-wider text-slate-300">OU Z-Score</span>
          </div>
          <div>
            <div className={`text-2xl font-bold font-mono ${Math.abs(state.z_score) > 2.0 ? 'text-purple-400' : 'text-slate-300'}`}>
              {state.z_score > 0 ? '+' : ''}{state.z_score.toFixed(3)}
            </div>
            <div className="text-xs text-slate-500 mt-1">Stochastic Spread Deviation</div>
          </div>
        </div>

        {/* Live PnL */}
        <div className="p-5 rounded-xl bg-slate-800/50 border border-slate-700/50 flex flex-col justify-between">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-yellow-500" />
            <span className="text-sm font-semibold uppercase tracking-wider text-slate-300">Unrealized PnL</span>
          </div>
          <div>
            <div className={`text-2xl font-bold font-mono ${state.unrealized_pnl > 0 ? 'text-emerald-400' : state.unrealized_pnl < 0 ? 'text-red-400' : 'text-slate-400'}`}>
              {state.unrealized_pnl > 0 ? '+' : ''}${state.unrealized_pnl.toFixed(2)}
            </div>
            <div className="text-xs text-slate-500 mt-1">Mark-to-Market</div>
          </div>
        </div>
      </div>

      {/* Active Position Info */}
      <div className="p-6 rounded-xl bg-slate-900 border border-slate-800">
        <h2 className="text-lg font-bold text-slate-200 mb-4">Live Position</h2>
        
        {isFlat ? (
          <div className="flex flex-col items-center justify-center p-8 border border-dashed border-slate-700 rounded-lg">
            <Scale className="w-8 h-8 text-slate-600 mb-2" />
            <div className="text-slate-400 font-medium">Flat. Waiting for stochastic divergence...</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {/* Long Leg */}
            <div className={`p-4 rounded-lg border ${isLong ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-red-500/30 bg-red-500/10'}`}>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">
                {isLong ? 'LONG LEG' : 'SHORT LEG'}
              </div>
              <div className="text-xl font-bold text-slate-200 mb-2">AVAX/USDT</div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Entry Price:</span>
                <span className="font-mono text-slate-300">${state.entry_price_y.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Quantity:</span>
                <span className="font-mono text-slate-300">{state.qty_y.toFixed(2)} AVAX</span>
              </div>
            </div>
            
            {/* Short Leg */}
            <div className={`p-4 rounded-lg border ${isShort ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-red-500/30 bg-red-500/10'}`}>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">
                {isShort ? 'LONG LEG' : 'SHORT LEG'}
              </div>
              <div className="text-xl font-bold text-slate-200 mb-2">SOL/USDT</div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Entry Price:</span>
                <span className="font-mono text-slate-300">${state.entry_price_x.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Quantity:</span>
                <span className="font-mono text-slate-300">{state.qty_x.toFixed(2)} SOL</span>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
